<script setup lang="ts">
import PublicPageLayout from "~/components/public/PublicPageLayout.vue";

const { t, locale } = useI18n();
const localeRoute = useLocaleRoute();

const operatorName = "Anton Rubchanka";
const operatorEmail = "info@senta.lt";

// TODO: Add a real correspondence address
// Example: "Street 1-2, LT-00000 Vilnius, Lithuania"
const correspondenceAddress = "";

const updatedAt = computed(() =>
  new Intl.DateTimeFormat(locale.value, {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(new Date("2026-08-18T00:00:00Z")),
);

const sections = [
  {
    key: "general",
    blocks: [
      { paragraph: "p1" },
      { paragraph: "p2" },
      { paragraph: "p3" },
      { paragraph: "p4" },
    ],
  },
  {
    key: "definitions",
    intro: ["p1"],
    blocks: [
      { item: "user" },
      { item: "client" },
      { item: "professional" },
      { item: "content" },
      { item: "platform_services" },
    ],
  },
  {
    key: "role",
    blocks: [
      { paragraph: "p1" },
      {
        paragraph: "p2",
        children: [
          "no_services",
          "no_contract",
          "no_employment",
          "no_payments",
          "direct_agreement",
        ],
      },
      { paragraph: "p3" },
    ],
  },
  {
    key: "accounts",
    blocks: [
      { paragraph: "p1", children: ["age"] },
      { paragraph: "p2", children: ["accurate", "security"] },
      { paragraph: "p3", children: ["authority"] },
    ],
  },
  {
    key: "clients",
    blocks: [
      { paragraph: "p1" },
      {
        paragraph: "p2",
        children: ["scope", "price", "timing", "qualification", "payment"],
      },
    ],
  },
  {
    key: "professionals",
    blocks: [
      {
        paragraph: "p1",
        children: ["lawful", "accurate", "qualifications", "rights"],
      },
      { paragraph: "p2", children: ["company"] },
      { paragraph: "p3" },
      { paragraph: "p4" },
    ],
  },
  {
    key: "content",
    blocks: [
      { paragraph: "p1" },
      { paragraph: "p2" },
      { paragraph: "p3" },
      { paragraph: "p4" },
      { paragraph: "p5" },
      { item: "illegal" },
      { item: "misleading" },
      { item: "rights" },
      { item: "abusive" },
      { item: "spam" },
    ],
  },
  {
    key: "prices",
    blocks: [{ paragraph: "p1" }, { paragraph: "p2" }, { paragraph: "p3" }],
  },
  {
    key: "reviews",
    blocks: [
      { paragraph: "p1" },
      {
        paragraph: "p2",
        children: ["fake", "self", "manipulation", "abusive", "unrelated"],
      },
      { paragraph: "p3" },
      { paragraph: "p4" },
      { paragraph: "p5" },
    ],
  },
  {
    key: "ranking",
    blocks: [
      {
        paragraph: "p1",
        children: [
          "relevance",
          "filters",
          "profile_quality",
          "freshness",
          "sorting",
        ],
      },
      { paragraph: "p2" },
      { paragraph: "p3" },
      { paragraph: "p4" },
      { paragraph: "p5" },
    ],
  },
  {
    key: "prohibited",
    blocks: [
      {
        paragraph: "p1",
        children: [
          "illegal",
          "fraud",
          "scraping",
          "automation",
          "multiple_accounts",
          "security",
          "database",
          "personal_data",
        ],
      },
    ],
  },
  {
    key: "moderation",
    blocks: [
      { paragraph: "p1" },
      {
        paragraph: "p2",
        children: ["correct", "hide", "remove", "restrict", "suspend"],
      },
      { paragraph: "p3" },
      { paragraph: "p4" },
      { paragraph: "p5" },
      { paragraph: "p6" },
      { paragraph: "p7" },
    ],
  },
  {
    key: "liability",
    blocks: [
      { paragraph: "p1" },
      {
        paragraph: "p2",
        children: [
          "quality",
          "information",
          "agreements",
          "availability",
          "user_actions",
        ],
      },
      { paragraph: "p3" },
      { paragraph: "p4" },
    ],
  },
  {
    key: "changes",
    blocks: [
      { paragraph: "p1" },
      { paragraph: "p2" },
      { paragraph: "p3" },
      { paragraph: "p4" },
      { paragraph: "p5" },
      { paragraph: "p6" },
      { paragraph: "p7" },
    ],
  },
  {
    key: "privacy_law",
    blocks: [
      { paragraph: "p1" },
      { paragraph: "p2" },
      { paragraph: "p3" },
      { paragraph: "p4" },
      { paragraph: "p5" },
      { paragraph: "p6" },
      { paragraph: "p7" },
    ],
    privacyLink: true,
  },
] as const;

const { canonicalUrl, currentLocale } = useLocalizedSeoLinks("/terms");

useSeoMeta({
  title: () => t("public_pages.terms.seo.title"),
  description: () => t("public_pages.terms.seo.description"),
  robots: "index, follow",
  ogTitle: () => t("public_pages.terms.seo.title"),
  ogDescription: () => t("public_pages.terms.seo.description"),
  ogType: "website",
  ogUrl: () => canonicalUrl.value,
  ogLocale: () => currentLocale.value.ogLocale,
  ogSiteName: "Senta.lt",
});
</script>

<template>
  <PublicPageLayout
    :eyebrow="$t('public_pages.common.legal')"
    :title="$t('public_pages.terms.title')"
    :description="$t('public_pages.terms.description')"
  >
    <div class="mb-14 w-full border-y border-app-border py-8 sm:py-9">
      <div class="grid gap-7 sm:grid-cols-2 sm:gap-10">
        <div>
          <p class="text-xs font-bold uppercase tracking-[0.12em] text-primary">
            {{ $t("public_pages.terms.meta.operator") }}
          </p>
          <p class="mt-2 font-bold text-app-text">
            {{ operatorName }}
          </p>
          <p class="mt-1 text-sm text-app-muted">
            {{ $t("public_pages.terms.meta.individual") }}
          </p>
          <p v-if="correspondenceAddress" class="mt-1 text-sm text-app-muted">
            {{ $t("public_pages.terms.meta.address") }}:
            {{ correspondenceAddress }}
          </p>
          <a
            :href="`mailto:${operatorEmail}`"
            class="mt-1 inline-block text-sm font-medium text-primary animated-underline"
          >
            {{ operatorEmail }}
          </a>
        </div>

        <div>
          <p class="text-xs font-bold uppercase tracking-[0.12em] text-primary">
            {{ $t("public_pages.terms.meta.updated") }}
          </p>
          <p class="mt-2 font-bold text-app-text">
            {{ updatedAt }}
          </p>
          <p class="mt-1 text-sm leading-6 text-app-muted">
            {{ $t("public_pages.terms.meta.language_note") }}
          </p>
        </div>
      </div>
    </div>

    <div class="space-y-14 sm:space-y-16">
      <section
        v-for="(section, sectionIndex) in sections"
        :key="section.key"
        class="w-full"
      >
        <h2 class="text-xl font-bold tracking-tight text-app-text sm:text-2xl">
          <span class="mr-2 text-primary"> {{ sectionIndex + 1 }}. </span>
          {{ $t(`public_pages.terms.sections.${section.key}.title`) }}
        </h2>

        <div
          class="mt-5 space-y-4 text-[15px] leading-7 text-app-muted sm:text-base"
        >
          <p
            v-for="introParagraph in 'intro' in section ? section.intro : []"
            :key="introParagraph"
          >
            {{
              $t(`public_pages.terms.sections.${section.key}.${introParagraph}`)
            }}
          </p>

          <div
            v-for="(block, blockIndex) in section.blocks"
            :key="`${section.key}-${blockIndex}`"
            class="space-y-3"
          >
            <p class="pl-12 -indent-12">
              <span
                class="inline-block w-12 indent-0 font-semibold tabular-nums text-primary"
              >
                {{ sectionIndex + 1 }}.{{ blockIndex + 1 }}.
              </span>

              <template v-if="'paragraph' in block">
                {{
                  $t(
                    `public_pages.terms.sections.${section.key}.${block.paragraph}`,
                  )
                }}
              </template>
              <template v-else>
                {{
                  $t(
                    `public_pages.terms.sections.${section.key}.bullets.${block.item}`,
                  )
                }}
              </template>
            </p>

            <div
              v-if="'children' in block && block.children?.length"
              class="space-y-2 pl-5 sm:pl-8"
            >
              <p
                v-for="(child, childIndex) in block.children"
                :key="child"
                class="pl-16 -indent-16"
              >
                <span
                  class="inline-block w-16 indent-0 font-semibold tabular-nums text-primary"
                >
                  {{ sectionIndex + 1 }}.{{ blockIndex + 1 }}.{{
                    childIndex + 1
                  }}.
                </span>
                {{
                  $t(
                    `public_pages.terms.sections.${section.key}.bullets.${child}`,
                  )
                }}
              </p>
            </div>
          </div>

          <NuxtLink
            v-if="'privacyLink' in section && section.privacyLink"
            :to="localeRoute('/privacy-policy') || '/privacy-policy'"
            class="inline-flex font-bold text-primary animated-underline"
          >
            {{ $t("public_pages.terms.privacy_link") }}
          </NuxtLink>
        </div>
      </section>
    </div>
  </PublicPageLayout>
</template>
